import { css, injectGlobal } from "@emotion/css";
import { atFrame } from "eutopia.ts";
import type { FeatureCollection, Geometry } from "geojson";
import { $, onCleanup, resolve, signal } from "signlets";

import { delay } from "../common";
import { decodeDrop, dropBounds, encodeDrop } from "../drop";
import { ground } from "../elevation";
import { createMavlink } from "../mavlink";
import { type Bounds, defaultDrop, type Drop, type Mode } from "../model";
import { positionEmpty } from "../position";
import { createSerialChannel } from "../serial";
import { createSimulationChannel } from "../simulation";
import { createVehicle, type Vehicle } from "../vehicle";
import { exportFile, importFile } from "../world/browser";
import { Compass } from "./Compass";
import { Controls } from "./Controls";
import { DropResults } from "./DropResults";
import { Introduction } from "./Introduction";
import { Message } from "./Message";
import { Preflight } from "./Preflight";
import { Sidebar } from "./Sidebar";
import { Telemetry } from "./Telemetry";
import { WeightBalance } from "./WeightBalance";
import { World } from "./World";

injectGlobal`
  @font-face {
    font-family: 'Exo 2';
    src: url(https://fonts.gstatic.com/s/exo2/v24/7cHmv4okm5zmbtYoK-4W4nIp.woff2) format('woff2');
  }

  :root {
      --background: black;
      font-size: 16px;
      font-family: "Exo 2", sans-serif;
      font-variant: tabular-nums;
  }

  html {
      background: var(--background);
      color: white;
      font-size: 16px;
      overscroll-behavior: none;
  }

  body {
      margin: 0;
      overscroll-behavior: none;
      overflow: hidden;
      color-scheme: dark;
  }
`;

const style = css`
  width: 100vw;
  height: 100vh;
  overflow: hidden;
`;

export const App = () => {
  const [mode, setMode] = signal<Mode>("introduction");
  const [vehicle, setVehicle] = signal<Vehicle | undefined>(undefined);
  const [drop, setDrop] = signal<Drop>(defaultDrop);
  const [filename, setFilename] = signal("");
  const [connected, setConnected] = signal(false);
  const [loading, setLoading] = signal(false);
  const [bounds, setBounds] = signal<Bounds | undefined>(undefined);
  const [overlay, setOverlay] = signal<FeatureCollection<Geometry> | undefined>(
    undefined,
  );

  const connecting = () => loading() || (!!vehicle() && !connected());

  const onDone = () => {
    if (mode() === "weight" && positionEmpty(drop().start)) setMode("start");
    else if (mode() === "start" && positionEmpty(drop().end)) {
      setMode("end");
      setBounds(dropBounds(drop()));
    } else if (mode() === "end" && positionEmpty(drop().drop)) setMode("drop");
    else setMode("edit");
  };

  const onChangeDrop = (_: Partial<Drop>) => setDrop({ ...drop(), ..._ });

  const onSimulate = async () => {
    vehicle()?.close();
    setLoading(true);
    const _vehicle = await createVehicle(
      createMavlink(
        await createSimulationChannel({ position: await ground(drop().drop) }),
      ),
    );
    await _vehicle.writeDrop(drop());
    await delay(2000);
    await _vehicle.arm();
    setLoading(false);
    setVehicle(_vehicle);
  };

  const onNew = () => {
    setDrop(defaultDrop);
    setFilename("new-drop.sa");
    setMode("weight");
  };

  const onLoad = async () => {
    const file = await importFile(".sa");
    const drop = await decodeDrop(file);
    if (!drop) return;
    setFilename(file.name);
    setDrop(drop);
    setBounds(dropBounds(drop));
    setMode("edit");
  };

  const onSave = () => {
    exportFile(encodeDrop(drop()), resolve(filename));
  };

  const onWeightBalance = () => setMode("weight");

  const onPreflight = async ({ simulation }: { simulation?: boolean } = {}) => {
    try {
      vehicle()?.close();
      setLoading(true);
      const _vehicle = await createVehicle(
        createMavlink(
          simulation
            ? await createSimulationChannel({
                position: await ground(drop().drop),
              })
            : await createSerialChannel(),
        ),
      );
      await _vehicle.writeDrop(drop());
      setVehicle(_vehicle);
      setMode("preflight");
    } catch (error) {
      console.warn("Connection failed", error);
    } finally {
      setLoading(false);
    }
  };

  const interval = setInterval(
    () => setConnected(Date.now() - (vehicle()?.state().time ?? 0) < 5000),
    200,
  );

  onCleanup(() => clearInterval(interval));

  const state = atFrame($(() => vehicle()?.state()));

  return (
    <div class={style}>
      <World
        state={state}
        drop={drop}
        mode={mode}
        bounds={bounds}
        overlay={overlay}
        onChangeDrop={onChangeDrop}
        onDone={onDone}
      />
      <Sidebar
        visible={$(() => mode() !== "introduction")}
        drop={drop}
        onChangeDrop={onChangeDrop}
        onStart={() => setMode("start")}
        onEnd={() => setMode("end")}
        onDrop={() => setMode("drop")}
        onDone={onDone}
        onSimulate={onSimulate}
        onLoad={onLoad}
        onSave={onSave}
        onWeightBalance={onWeightBalance}
        onPreflight={onPreflight}
        onClose={() => setMode("introduction")}
      />
      <Controls onGeocode={setBounds} onOverlay={setOverlay} />
      <Compass />
      <Message visible={$(() => !connecting() && mode() === "start")}>
        Select drop zone start
      </Message>
      <Message visible={$(() => !connecting() && mode() === "end")}>
        Select drop zone end
      </Message>
      <Message visible={$(() => !connecting() && mode() === "drop")}>
        Select drop position
      </Message>
      <Message visible={$(() => !connecting() && !!state()?.flying)}>
        <Telemetry state={state} drop={drop} />
      </Message>
      <Message visible={$(() => connecting())}>Connecting...</Message>
      <DropResults
        visible={$(() => !connecting() && !mode())}
        vehicle={vehicle}
      />
      <Introduction
        visible={$(() => mode() === "introduction")}
        onNew={onNew}
        onLoad={onLoad}
      />
      <WeightBalance
        visible={$(() => mode() === "weight")}
        drop={drop}
        onChangeDrop={onChangeDrop}
        onNext={onDone}
      />
      <Preflight
        visible={$(() => mode() === "preflight")}
        vehicle={vehicle}
        onClose={onDone}
      />
    </div>
  );
};
